"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const util_1 = require("util");
const model_1 = __importDefault(require("../base/model"));
const agent_schema_1 = require("./agent.schema");
class Agent extends model_1.default {
    constructor() {
        super(...arguments);
        this._updates = [];
    }
    _addUpdate(field) {
        if (this.ID === 'new')
            return;
        if (this._updates && !this._updates.includes(field)) {
            this._updates.push(field);
        }
    }
    async _updateInternals(data) {
        let hasUpdates = false;
        if (data.routingProfileID && this.routingProfileID !== data.routingProfileID) {
            hasUpdates = true;
            this._routingProfileID = data.routingProfileID;
        }
        if (data.hierarchyGroupID && this.hierarchyGroupID !== data.hierarchyGroupID) {
            this._addUpdate('hierarchyGroupID');
            hasUpdates = true;
            this._hierarchyGroupID = data.hierarchyGroupID;
        }
        if (data.securityGroupIDs &&
            (this.securityGroupIDs || []).toString() !== data.securityGroupIDs.toString()) {
            this._addUpdate('securityGroupIDs');
            hasUpdates = true;
            this._securityGroupIDs = data.securityGroupIDs;
        }
        if (data.email && this.email !== data.email) {
            hasUpdates = true;
            this._email = data.email;
        }
        if (data.firstName && this.firstName !== data.firstName) {
            hasUpdates = true;
            this._firstName = data.firstName;
        }
        if (data.lastName && this.lastName !== data.lastName) {
            hasUpdates = true;
            this._lastName = data.lastName;
        }
        if (data.afterWorkTimeLimit && this.afterWorkTimeLimit !== data.afterWorkTimeLimit) {
            hasUpdates = true;
            this._afterWorkTimeLimit = data.afterWorkTimeLimit;
        }
        if (data.autoAccept && this.autoAccept !== data.autoAccept) {
            hasUpdates = true;
            this._autoAccept = data.autoAccept;
        }
        if (data.phoneType && this.phoneType !== data.phoneType) {
            hasUpdates = true;
            this._phoneType = data.phoneType;
        }
        if (data.deskPhoneNumber && this.deskPhoneNumber !== data.deskPhoneNumber) {
            hasUpdates = true;
            this._deskPhoneNumber = data.deskPhoneNumber;
        }
        if (data.tags && !(0, util_1.isDeepStrictEqual)(this.tags, data.tags)) {
            hasUpdates = true;
            this._tags = data.tags;
        }
        if (hasUpdates) {
            await this.commit();
        }
    }
    clearUpdates() {
        this._updates = [];
    }
    getUpdates() {
        return this._updates;
    }
    hasUpdates() {
        return this._updates.length > 0;
    }
    set routingProfileID(routingProfileID) {
        if (this._routingProfileID && routingProfileID !== this._routingProfileID) {
            this._addUpdate('routingProfileID');
        }
        this._routingProfileID = routingProfileID;
    }
    get routingProfileID() {
        return this._routingProfileID;
    }
    set hierarchyGroupID(hierarchyGroupID) {
        if (this._hierarchyGroupID) {
            this._addUpdate('hierarchyGroupID');
        }
        this._hierarchyGroupID = hierarchyGroupID;
    }
    get hierarchyGroupID() {
        return this._hierarchyGroupID;
    }
    set securityGroupIDs(securityGroupIDs) {
        if (this._securityGroupIDs &&
            this._securityGroupIDs.length &&
            securityGroupIDs.toString() !== this._securityGroupIDs.toString()) {
            this._addUpdate('securityGroupIDs');
        }
        this._securityGroupIDs = securityGroupIDs;
    }
    get securityGroupIDs() {
        return this._securityGroupIDs;
    }
    get email() {
        return this._email;
    }
    set email(email) {
        if (this._email && this._email !== email) {
            this._addUpdate('identityInfo');
        }
        this._email = email;
    }
    get firstName() {
        return this._firstName;
    }
    set firstName(firstName) {
        if (this._firstName && this._firstName !== firstName) {
            this._addUpdate('identityInfo');
        }
        this._firstName = firstName;
    }
    get lastName() {
        return this._lastName;
    }
    set lastName(lastName) {
        if (this._lastName && this._lastName !== lastName) {
            this._addUpdate('identityInfo');
        }
        this._lastName = lastName;
    }
    get mobileNumber() {
        return this._mobileNumber;
    }
    set mobileNumber(mobileNumber) {
        this._mobileNumber = mobileNumber;
    }
    get afterWorkTimeLimit() {
        return this._afterWorkTimeLimit;
    }
    set afterWorkTimeLimit(afterWorkTimeLimit) {
        if (this._afterWorkTimeLimit && this._afterWorkTimeLimit !== afterWorkTimeLimit) {
            this._addUpdate('phoneConfig');
        }
        this._afterWorkTimeLimit = afterWorkTimeLimit;
    }
    get phoneType() {
        return this._phoneType;
    }
    set phoneType(phoneType) {
        if (this._phoneType && this._phoneType !== phoneType) {
            this._addUpdate('phoneConfig');
        }
        this._phoneType = phoneType;
    }
    get autoAccept() {
        return this._autoAccept;
    }
    set autoAccept(autoAccept) {
        if (this._autoAccept !== autoAccept) {
            this._addUpdate('phoneConfig');
        }
        this._autoAccept = autoAccept;
    }
    get deskPhoneNumber() {
        return this._deskPhoneNumber;
    }
    set deskPhoneNumber(deskPhoneNumber) {
        if (this.deskPhoneNumber && this._deskPhoneNumber !== deskPhoneNumber) {
            this._addUpdate('phoneConfig');
        }
        this._deskPhoneNumber = deskPhoneNumber;
    }
    get mfa() {
        return this._mfa;
    }
    set mfa(mfa) {
        this._mfa = mfa;
    }
    get externalID() {
        return this._externalID;
    }
    set externalID(externalID) {
        this._externalID = externalID;
    }
    get disabled() {
        return this._disabled;
    }
    set disabled(disabled) {
        this._disabled = disabled;
    }
    get tags() {
        return this._tags;
    }
    set tags(tags) {
        this._tags = tags;
    }
    async preCommit() {
        if (!this.securityRoles || this._updates.includes('securityGroupIDs') ||
            //Each security group should be a role so the length should match
            ((this.securityRoles || []).length !== this._securityGroupIDs.length)) {
            this.securityRoles = await this._mapper.determineSecurityRoles(this);
        }
        if (!this.hierarchyStructure || this._updates.includes('hierarchyGroupID')) {
            this.hierarchyStructure = await this._mapper.determineHierarchyStructure(this);
        }
    }
    toDB() {
        const db = super.toDB();
        db.routingProfileID = this.routingProfileID;
        db.securityGroupIDs = this.securityGroupIDs;
        db.hierarchyGroupID = this.hierarchyGroupID;
        db.email = this.email;
        db.firstName = this.firstName;
        db.lastName = this.lastName;
        db.mobileNumber = this.mobileNumber;
        db.afterWorkTimeLimit = this.afterWorkTimeLimit;
        db.phoneType = this.phoneType;
        db.autoAccept = this.autoAccept;
        db.deskPhoneNumber = this.deskPhoneNumber;
        db.mfa = this.mfa;
        db.externalID = this.externalID;
        db.disabled = this.disabled;
        db.tags = this.tags;
        return db;
    }
    serialize() {
        const obj = super.serialize();
        obj.routingProfileID = this.routingProfileID;
        obj.securityGroupIDs = this.securityGroupIDs;
        obj.hierarchyGroupID = this.hierarchyGroupID;
        obj.email = this.email;
        obj.firstName = this.firstName;
        obj.lastName = this.lastName;
        obj.mobileNumber = this.mobileNumber;
        obj.afterWorkTimeLimit = this.afterWorkTimeLimit;
        obj.phoneType = this.phoneType;
        obj.autoAccept = this.autoAccept;
        obj.deskPhoneNumber = this.deskPhoneNumber;
        obj.mfa = this.mfa;
        obj.externalID = this.externalID;
        obj.disabled = this.disabled;
        obj.tags = this.tags;
        return obj;
    }
}
exports.default = Agent;
Agent.schema = agent_schema_1.AgentSchema;
Agent.readonly = ['companyID', 'instanceID', 'username'];
